// APP_INTENT:CLOSE_SESSION — how a hybrid answer says "the engineer is done".
//
// Guided mode ends a session through the agent's own close_session tool. Hybrid
// answers come from one tool-less streaming pass (faststage.build_fast_agent),
// so the model has no tool to call and no way to end a turn other than writing.
// It writes this checkpoint instead: the whole reply becomes
//
//     APP_INTENT:CLOSE_SESSION <one-paragraph wrap-up>
//
// and the client turns it into the tse:close_session event, which runs exactly
// the close the guided tool runs. The marker itself is never shown — the wrap-up
// after it is the message the engineer reads.
export const CLOSE_INTENT_MARKER = "APP_INTENT:CLOSE_SESSION";

/**
 * Split a (possibly partial) model reply into what to display and whether it
 * asked to close.
 *
 * Streaming makes the partial case real: the marker arrives a few characters at
 * a time, so "APP_IN" is not yet a close and must not be painted either — it
 * would flash raw protocol at the engineer and then vanish. While the text is
 * still a prefix of the marker, `text` comes back empty and `pending` is true.
 *
 * @param {string} raw everything the model has emitted for this message so far
 * @returns {{text: string, close: boolean, summary: string, pending: boolean}}
 */
export function splitCloseIntent(raw) {
  const value = typeof raw === "string" ? raw : "";
  const lead = value.replace(/^\s+/, "");
  if (!lead) return { text: value, close: false, summary: "", pending: false };

  const at = lead.indexOf(CLOSE_INTENT_MARKER);
  if (at === 0) {
    const summary = lead.slice(CLOSE_INTENT_MARKER.length).replace(/^[\s:—-]+/, "");
    return { text: summary, close: true, summary, pending: false };
  }
  // Mid-text marker: the model answered and then asked to close. Honour both —
  // show the answer with the marker cut out, and still close.
  if (at > 0) {
    const summary = lead.slice(at + CLOSE_INTENT_MARKER.length).replace(/^[\s:—-]+/, "");
    return { text: (lead.slice(0, at) + "\n\n" + summary).trim(), close: true, summary, pending: false };
  }
  // Still arriving: hold back anything that could still become the marker.
  if (CLOSE_INTENT_MARKER.startsWith(lead)) {
    return { text: "", close: false, summary: "", pending: true };
  }
  return { text: value, close: false, summary: "", pending: false };
}

/** True when a finished reply carries the checkpoint. */
export const hasCloseIntent = (raw) => splitCloseIntent(raw).close;
